import React, { useEffect, useState, useCallback, useRef } from "react";
import { ShieldAlert, AlertTriangle, Lock, MessageSquare, CheckCircle2 } from "lucide-react";
import { apiPost } from "../lib/api";
import { getI18n } from "../lib/i18n";

/**
 * Anti-piracy strike controller.
 *
 * Usage:
 *   const sec = useSecurityStrikes({ lang, lessonId });
 *   <SecurityWarningModal sec={sec} />
 *   ...
 *   sec.report("right_click");
 *
 * Flow per modal:
 *  1. Suspicious event detected → `sec.report(type)` fires.
 *  2. Backend records event with status="confirmed" and returns severity.
 *  3. Modal opens with two CTAs:
 *      - "Ho capito" → just closes; strike already counted.
 *      - "Reclama"   → reveals an appeal form; on submit, backend flips
 *                      the event to status="pending_review" and the strike
 *                      is excluded from the block count until an admin decides.
 *  4. When the user is on the "blocked" severity, the appeal CTA is hidden
 *     and the only option is "Vai alla home".
 */
export function useSecurityStrikes({ lang, lessonId }) {
  const [state, setState] = useState({
    open: false,
    severity: "warn",
    strikeCount: 0,
    threshold: 3,
    eventId: null,
  });
  const shownSeveritiesRef = useRef(new Set());

  const close = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
  }, []);

  const report = useCallback(async (event_type, meta = null) => {
    try {
      const res = await apiPost("/security/strike", {
        event_type, lesson_id: lessonId || null, meta,
      });
      const severity = res.severity || "warn";
      if (!shownSeveritiesRef.current.has(severity)) {
        shownSeveritiesRef.current.add(severity);
        setState({
          open: true,
          severity,
          strikeCount: res.strike_count || 0,
          threshold: res.threshold || 3,
          eventId: res.event_id || null,
        });
      }
    } catch (_err) {
      // Silent
    }
  }, [lessonId]);

  return { ...state, report, close, lang };
}

function AppealForm({ eventId, t, onClose, onSubmitted }) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!text.trim()) {
      setError(t.appeal_error_required);
      return;
    }
    setSubmitting(true);
    try {
      await apiPost(`/security/strike/${encodeURIComponent(eventId)}/appeal`, {
        explanation: text.trim(),
      });
      onSubmitted();
    } catch (err) {
      setError(t.appeal_error_generic);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit}
          data-testid="security-appeal-form"
          className="space-y-3">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4" style={{ color: "#7B5CE0" }} />
        <h3 className="text-base font-semibold">{t.appeal_title}</h3>
      </div>
      <p className="text-[13px] leading-relaxed opacity-85">{t.appeal_intro}</p>
      <textarea
        data-testid="security-appeal-textarea"
        rows={4}
        placeholder={t.appeal_placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={submitting}
        autoFocus
        maxLength={2000}
        className="w-full px-3 py-2 rounded-xl text-sm text-white"
        style={{
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.2)",
          outline: "none",
          resize: "vertical",
        }}
      />
      {error ? (
        <p data-testid="security-appeal-error"
           className="text-xs" style={{ color: "#F87171" }}>{error}</p>
      ) : null}
      <div className="flex gap-2">
        <button
          type="button"
          data-testid="security-appeal-cancel"
          onClick={onClose}
          disabled={submitting}
          className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium"
          style={{
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.18)",
            color: "white",
            opacity: submitting ? 0.6 : 1,
          }}
        >
          {t.appeal_cancel}
        </button>
        <button
          type="submit"
          data-testid="security-appeal-submit"
          disabled={submitting}
          className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium"
          style={{
            background: "#7B5CE0", color: "white",
            opacity: submitting ? 0.7 : 1,
          }}
        >
          {submitting ? t.appeal_submitting : t.appeal_submit}
        </button>
      </div>
    </form>
  );
}

export function SecurityWarningModal({ sec }) {
  const t = getI18n(sec.lang).security;
  // local view state for the appeal flow inside this modal
  const [view, setView] = useState("warning"); // warning | appeal | submitted

  // Reset internal view when the modal closes/reopens with new event
  useEffect(() => {
    if (sec.open) setView("warning");
  }, [sec.open, sec.eventId]);

  if (!sec.open) return null;

  const title = sec.severity === "blocked"
    ? t.modal_title_3
    : sec.severity === "severe" ? t.modal_title_2 : t.modal_title_1;
  const body = sec.severity === "blocked"
    ? t.modal_body_3
    : sec.severity === "severe" ? t.modal_body_2 : t.modal_body;
  const Icon = sec.severity === "blocked" ? Lock
            : sec.severity === "severe" ? AlertTriangle : ShieldAlert;
  const accent = sec.severity === "blocked" ? "#E11D48"
              : sec.severity === "severe" ? "#F59E0B" : "#7B5CE0";

  const handleAck = () => {
    if (sec.severity === "blocked") {
      window.location.href = "/";
    } else {
      sec.close();
    }
  };

  return (
    <div data-testid="security-warning-modal"
         role="alertdialog"
         aria-modal="true"
         className="fixed inset-0 z-[10000] grid place-items-center px-4"
         style={{ background: "rgba(8,4,24,0.78)", backdropFilter: "blur(10px)" }}
         onContextMenu={(e) => e.preventDefault()}>
      <div className="relative w-full max-w-lg rounded-3xl p-7 shadow-2xl"
           style={{
             background: "linear-gradient(135deg, #1a0f3e 0%, #2a1750 100%)",
             border: `1px solid ${accent}55`,
             color: "white",
           }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="h-12 w-12 rounded-2xl grid place-items-center"
               style={{ background: `${accent}22`, border: `1px solid ${accent}88` }}>
            <Icon className="h-6 w-6" style={{ color: accent }} />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] opacity-70">
              {t.modal_eyebrow}
            </p>
            <h2 className="text-xl font-semibold leading-tight">{title}</h2>
          </div>
        </div>

        {view === "warning" ? (
          <>
            <p className="text-sm leading-relaxed opacity-95 mb-4">{body}</p>
            <div className="text-xs mb-5 opacity-70">
              {t.strike_count_label}:{" "}
              <span className="font-mono">{sec.strikeCount} / {sec.threshold}</span>
            </div>
            <div className="flex flex-col gap-2">
              <button
                data-testid="security-warning-ack"
                onClick={handleAck}
                className="w-full px-5 py-3 rounded-xl text-sm font-medium transition-transform hover:scale-[1.01]"
                style={{ background: accent, color: "white" }}
              >
                {sec.severity === "blocked" ? t.modal_blocked_cta : t.modal_ack}
              </button>
              {sec.severity !== "blocked" && sec.eventId ? (
                <button
                  data-testid="security-warning-appeal"
                  onClick={() => setView("appeal")}
                  className="w-full px-5 py-2.5 rounded-xl text-xs font-medium"
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    color: "white",
                  }}
                >
                  {t.modal_appeal_btn}
                </button>
              ) : null}
            </div>
          </>
        ) : null}

        {view === "appeal" && sec.eventId ? (
          <AppealForm
            eventId={sec.eventId}
            t={t}
            onClose={() => setView("warning")}
            onSubmitted={() => setView("submitted")}
          />
        ) : null}

        {view === "submitted" ? (
          <div data-testid="security-appeal-success" className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 shrink-0"
                            style={{ color: "#22C55E" }} />
              <p className="text-sm leading-relaxed opacity-95">
                {t.appeal_success}
              </p>
            </div>
            <button
              data-testid="security-appeal-success-close"
              onClick={sec.close}
              className="w-full px-5 py-3 rounded-xl text-sm font-medium"
              style={{ background: "#7B5CE0", color: "white" }}
            >
              {t.appeal_close}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
